import { useEffect, useState } from "react";
import { useSetRecoilState } from "recoil";

import {
  ActivePlaylistClipState,
  PlaylistClipsState,
  PlaylistIdState,
  VisiblePlaylistClipsState,
} from "MVVM/State/embeddablePlaylist";
import { formatPlaylistClip } from "../../Utilities";
import { analytics } from "MVVM/Analytics";

interface IPlaylistFetchParams {
  playlist: string;
  platform: string;
}

export const GenericEmbeddablePlaylistViewModel = ({
  playlist,
  platform,
}: IPlaylistFetchParams) => {
  const [isLoading, setIsLoading] = useState(true);
  const setPlaylistClips = useSetRecoilState(PlaylistClipsState);
  const setActivePlaylistClip = useSetRecoilState(ActivePlaylistClipState);
  const setPlaylistId = useSetRecoilState(PlaylistIdState);
  const setVisiblePlaylistClips = useSetRecoilState(VisiblePlaylistClipsState);

  useEffect(() => {
    const encodedPlatform = encodeURIComponent(platform);
    const url = `${process.env.REACT_APP_PARTNER_URL}/playlist?name=${playlist}&platform=${encodedPlatform}&fmt=iframe`;

    async function fetchData() {
      setIsLoading(true);
      try {
        const res = await fetch(url, {
          headers: {
            "Content-Type": "application/json",
          },
        });

        if (!res.ok) {
          return;
        }

        const data = await res.json();

        if (!data?.data?.clips?.length) {
          return;
        }

        setPlaylistId(playlist);
        setPlaylistClips(data.data.clips);

        const initialVisibleClips = data.data.clips.slice(0, 5);
        setVisiblePlaylistClips(initialVisibleClips);

        const firstClip = data.data.clips[0];
        const formattedClip = formatPlaylistClip(firstClip);
        setActivePlaylistClip(formattedClip);
      } catch (error) {
        analytics.track("Generic Playlist Fetch Error", {
          locationIdentifier: "generic_playlist_fetch_error",
          playlist,
          error: (error as Error).message,
        });
      } finally {
        setIsLoading(false);
      }
    }

    if (playlist && platform) {
      fetchData();
    }
  }, [
    playlist,
    platform,
    setPlaylistClips,
    setActivePlaylistClip,
    setPlaylistId,
    setVisiblePlaylistClips,
  ]);

  return { isLoading };
};
